/**
 * Servicio para operaciones CRUD de productos en Firebase
 */
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  addDoc,
  deleteDoc,
  writeBatch 
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { COLLECTIONS } from '../constants';

/**
 * Obtiene todos los productos
 * @returns {Promise<Array>} Lista de productos
 */
export const obtenerProductos = async () => {
  try {
    const snap = await getDocs(collection(db, COLLECTIONS.PRODUCTOS));
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error al obtener productos:', error);
    throw new Error('No se pudieron cargar los productos');
  }
};

/**
 * Obtiene un producto por ID
 * @param {string} id - ID del producto
 * @returns {Promise<Object|null>} Producto o null si no existe
 */
export const obtenerProductoPorId = async (id) => {
  if (!id) {
    throw new Error('ID de producto no válido');
  }

  try {
    const productoRef = doc(db, COLLECTIONS.PRODUCTOS, id);
    const productoSnap = await getDoc(productoRef);

    if (!productoSnap.exists()) {
      return null;
    }

    return { id: productoSnap.id, ...productoSnap.data() };
  } catch (error) {
    console.error('Error al obtener producto:', error);
    throw new Error('Error al cargar el producto');
  }
};

/**
 * Crea un nuevo producto
 * @param {Object} datos - Datos del producto
 * @returns {Promise<string>} ID del producto creado
 */
export const crearProducto = async (datos) => {
  try {
    // Limpiar campos null/undefined/vacíos, pero mantener destacado si existe
    const datosLimpios = {};
    Object.keys(datos).forEach(key => {
      const valor = datos[key];
      // Incluir el campo si no es null/undefined/vacío, O si es el campo destacado (boolean)
      if (valor !== null && valor !== undefined && valor !== '') {
        datosLimpios[key] = valor;
      } else if (key === 'destacado' && typeof valor === 'boolean') {
        datosLimpios[key] = valor;
      }
    });

    console.log('Datos a crear:', datosLimpios);
    console.log('Cantidad de campos:', Object.keys(datosLimpios).length);
    
    // Crear el producto incluso si solo tiene el campo destacado
    const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTOS), datosLimpios);
    console.log('Producto creado exitosamente con ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error completo al crear producto:', error);
    console.error('Stack:', error.stack);
    throw new Error(error.message || 'Error al crear el producto');
  }
};

/**
 * Actualiza un producto
 * @param {string} id - ID del producto
 * @param {Object} datos - Datos a actualizar
 * @returns {Promise<void>}
 */
export const actualizarProducto = async (id, datos) => {
  if (!id) {
    throw new Error('ID de producto no válido');
  }

  try {
    const productoRef = doc(db, COLLECTIONS.PRODUCTOS, id);
    
    // Limpiar campos null/undefined/vacíos
    const datosLimpios = {};
    Object.keys(datos).forEach(key => {
      const valor = datos[key];
      if (valor !== null && valor !== undefined && valor !== '') {
        datosLimpios[key] = valor;
      }
    });

    await updateDoc(productoRef, datosLimpios);
  } catch (error) {
    console.error('Error al actualizar producto:', error);
    throw new Error('Error al actualizar el producto');
  }
};

/**
 * Elimina un producto
 * @param {string} id - ID del producto
 * @returns {Promise<void>}
 */
export const eliminarProducto = async (id) => {
  if (!id) {
    throw new Error('ID de producto no válido');
  }

  try {
    const productoRef = doc(db, COLLECTIONS.PRODUCTOS, id);
    await deleteDoc(productoRef);
  } catch (error) {
    console.error('Error al eliminar producto:', error);
    throw new Error('Error al eliminar el producto');
  }
};

/**
 * Actualiza precios de múltiples productos
 * @param {Array<string>} ids - IDs de los productos
 * @param {Object} precios - Objeto con precio y/o preciomenor
 * @returns {Promise<void>}
 */
export const actualizarPreciosMasivo = async (ids, precios) => {
  if (!ids || ids.length === 0) {
    throw new Error('No hay productos para actualizar');
  }

  try {
    const batch = writeBatch(db);
    const updateData = {};

    if (precios.precio !== null && precios.precio !== undefined && precios.precio !== '') {
      updateData.precio = Number(precios.precio);
    }
    if (precios.preciomenor !== null && precios.preciomenor !== undefined && precios.preciomenor !== '') {
      updateData.preciomenor = Number(precios.preciomenor);
    }

    if (Object.keys(updateData).length === 0) {
      throw new Error('No hay precios para actualizar');
    }

    ids.forEach(productoId => {
      const productoRef = doc(db, COLLECTIONS.PRODUCTOS, productoId);
      batch.update(productoRef, updateData);
    });

    await batch.commit();
  } catch (error) {
    console.error('Error al actualizar precios:', error);
    throw new Error('Error al actualizar los precios');
  }
};

